/**
 * SetupPasswordPage.tsx — /#/setup-password?token=...&slug=...
 * First-time admin password setup after org provisioning.
 */
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { sha256 } from '../api/worker'

const GOLD = '#c9a84c'
const NAVY = '#0a1628'
const WORKER_URL = (import.meta.env.VITE_API_URL || 'https://castellan-security.com').replace(/\/$/, '')

export default function SetupPasswordPage() {
  const navigate  = useNavigate()
  const [pw, setPw]         = useState('')
  const [pw2, setPw2]       = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError]   = useState('')
  const [done, setDone]     = useState(false)

  const params = new URLSearchParams(window.location.hash.split('?')[1] || '')
  const token  = params.get('token') || ''
  const slug   = params.get('slug')  || ''

  useEffect(() => {
    if (!token || !slug) setError('Invalid or missing setup link. Contact support@castellan-security.com.')
  }, [])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (pw.length < 8)       { setError('Password must be at least 8 characters'); return }
    if (pw !== pw2)          { setError('Passwords do not match'); return }
    setLoading(true); setError('')
    try {
      const hash = await sha256(pw)
      const res  = await fetch(`${WORKER_URL}/api/v1/auth/setup-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, slug, newPasswordHash: hash }),
      })
      const data = await res.json()
      if (!data.success) { setError(data.error || 'Setup failed. Try again.'); return }
      setDone(true)
    } catch {
      setError('Network error. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: NAVY, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      <div style={{ fontSize: 24, fontWeight: 800, color: GOLD, letterSpacing: 3, marginBottom: 32 }}>CASTELLAN</div>

      <div style={{ background: '#0f1a2e', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 16, padding: '36px 32px', width: '100%', maxWidth: 400 }}>
        {done ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>✅</div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#f1f5f9', marginBottom: 8 }}>Password set!</h2>
            <p style={{ color: '#64748b', fontSize: 14, marginBottom: 24 }}>You can now sign in with call sign <strong style={{ color: GOLD }}>ADMIN</strong>.</p>
            <button
              onClick={() => navigate('/login')}
              style={{ width: '100%', padding: '12px 0', background: GOLD, color: NAVY, border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 15, cursor: 'pointer' }}
            >Sign in →</button>
          </div>
        ) : (
          <>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: '#f1f5f9', marginBottom: 6, textAlign: 'center' }}>Set up your admin password</h1>
            <p style={{ color: '#64748b', fontSize: 13, textAlign: 'center', marginBottom: 28 }}>
              You're setting up the administrator account for your organization. Your call sign is <strong style={{ color: GOLD }}>ADMIN</strong>.
            </p>

            {error && <div style={{ background: 'rgba(239,68,68,0.08)', color: '#f87171', padding: '10px 12px', borderRadius: 6, fontSize: 13, marginBottom: 16 }}>{error}</div>}

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' as const, letterSpacing: '.07em', marginBottom: 6 }}>New password</div>
                <input
                  type="password" value={pw} onChange={e => setPw(e.target.value)}
                  placeholder="At least 8 characters"
                  style={{ width: '100%', background: '#0a1628', border: '1px solid #1e3050', borderRadius: 8, padding: '10px 14px', color: '#f1f5f9', fontSize: 14, outline: 'none', boxSizing: 'border-box' as const }}
                />
              </div>
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' as const, letterSpacing: '.07em', marginBottom: 6 }}>Confirm password</div>
                <input
                  type="password" value={pw2} onChange={e => setPw2(e.target.value)}
                  placeholder="Repeat password"
                  style={{ width: '100%', background: '#0a1628', border: '1px solid #1e3050', borderRadius: 8, padding: '10px 14px', color: '#f1f5f9', fontSize: 14, outline: 'none', boxSizing: 'border-box' as const }}
                />
              </div>
              <button
                type="submit" disabled={loading || !token || !slug}
                style={{ width: '100%', padding: '12px 0', background: loading ? '#64748b' : `linear-gradient(135deg, ${GOLD}, #d97706)`, color: NAVY, border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 15, cursor: loading ? 'not-allowed' : 'pointer' }}
              >{loading ? 'Setting up…' : 'Set password →'}</button>
            </form>
          </>
        )}
      </div>
    </div>
  )
}
